/**
 * ShareResultsView - Display share results with success/error feedback
 * Feature: 080-critique-sharing
 * Tasks: T056, T071
 *
 * Shows the results of sharing a critique session with recipients.
 * Displays success messages, errors, email status, and invite links for each recipient.
 * T071: Shows invite link with copy button and reliability warning for email recipients.
 */

import { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import type { ShareCritiqueResult, ShareCritiqueRecipientResult } from '@/hooks/graphql';
import { useDevFlags } from '@/contexts/DevFlagsContext';
import logger from '@/utils/logger';

// ============================================================================
// Types
// ============================================================================

export interface ShareResultsViewProps {
  /** The share results from the mutation */
  results: ShareCritiqueResult;
  /** Callback to dismiss and return to input state */
  onDismiss: () => void;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ShareResultsView({
  results,
  onDismiss,
  className,
}: ShareResultsViewProps) {
  const devFlags = useDevFlags();
  const debugMode = devFlags?.flags.debugUI ?? false;
  const allSucceeded = results.failureCount === 0;
  const someSucceeded = results.successCount > 0;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary header */}
      <div
        className={cn(
          'p-4 rounded-lg',
          allSucceeded
            ? 'bg-green-500/10 border border-green-500/20'
            : someSucceeded
              ? 'bg-yellow-500/10 border border-yellow-500/20'
              : 'bg-red-500/10 border border-red-500/20'
        )}
      >
        <div className="flex items-center gap-2">
          {allSucceeded ? (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          ) : someSucceeded ? (
            <AlertCircleIcon className="h-5 w-5 text-yellow-500" />
          ) : (
            <XCircleIcon className="h-5 w-5 text-red-500" />
          )}
          <h3
            className={cn(
              'font-medium',
              allSucceeded
                ? 'text-green-700 dark:text-green-400'
                : someSucceeded
                  ? 'text-yellow-700 dark:text-yellow-400'
                  : 'text-red-700 dark:text-red-400'
            )}
          >
            {results.message}
          </h3>
        </div>
      </div>

      {/* Individual results */}
      {results.results.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Recipients</h4>
          <div className="space-y-1">
            {results.results.map((result, index) => (
              <RecipientResultRow key={index} result={result} debugMode={debugMode} />
            ))}
          </div>
        </div>
      )}

      {/* Action button */}
      <button
        type="button"
        onClick={onDismiss}
        className={cn(
          'w-full py-2 px-4 rounded-md text-sm font-medium',
          'bg-primary text-primary-foreground hover:bg-primary/90',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
        )}
      >
        Done
      </button>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface RecipientResultRowProps {
  result: ShareCritiqueRecipientResult;
  debugMode?: boolean;
}

function RecipientResultRow({ result, debugMode = false }: RecipientResultRowProps) {
  const [copied, setCopied] = useState(false);
  const displayName = result.recipientId
    ? result.recipientId.substring(0, 12) + '...'
    : result.recipientEmail || 'Unknown';

  const isEmailRecipient = !!result.recipientEmail && !result.recipientId;
  const hasShareUrl = !!result.shareUrl;
  const emailFailed = result.emailStatus === 'failed';
  // In debug mode, always show invite link for any successful share with a URL
  const showInviteLink = hasShareUrl && (debugMode || isEmailRecipient);

  const handleCopyLink = useCallback(async () => {
    if (!result.shareUrl) return;
    try {
      await navigator.clipboard.writeText(result.shareUrl);
      setCopied(true);
      logger.info('Sharing', 'Invite link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Sharing', 'Failed to copy invite link', { error: err });
    }
  }, [result.shareUrl]);

  return (
    <div
      className={cn(
        'rounded-md text-sm',
        result.success
          ? 'bg-muted/50'
          : 'bg-red-500/5 border border-red-500/10'
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        {/* Status icon */}
        {result.success ? (
          <CheckIcon className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <XIcon className="h-4 w-4 text-red-500 shrink-0" />
        )}

        {/* Recipient name */}
        <span className="flex-1 min-w-0 truncate text-foreground">
          {result.recipientEmail || displayName}
        </span>

        {/* Email status badge */}
        {result.success && (
          <EmailStatusBadge status={result.emailStatus} />
        )}

        {/* Error message */}
        {!result.success && (
          <span className="text-xs text-red-500 truncate max-w-[150px]">
            {result.message}
          </span>
        )}
      </div>

      {/* Invite link section for email recipients (T071) or debug mode */}
      {result.success && showInviteLink && (
        <div className="px-3 pb-2 space-y-1.5">
          {/* Context message */}
          {debugMode && !isEmailRecipient ? (
            <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <DebugIcon className="h-3 w-3" />
              Debug: Invite link for testing
            </p>
          ) : emailFailed ? (
            <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
              <AlertIcon className="h-3 w-3" />
              Email delivery failed. Share the link below manually.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              If email doesn&apos;t arrive, share this link:
            </p>
          )}
          {/* Copy link row */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={result.shareUrl || ''}
              readOnly
              className={cn(
                'flex-1 text-xs px-2 py-1.5 rounded border',
                'bg-background border-border text-foreground',
                'focus:outline-none focus:ring-1 focus:ring-ring'
              )}
              onClick={(e) => e.currentTarget.select()}
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className={cn(
                'px-2 py-1.5 rounded text-xs font-medium shrink-0',
                'border border-border',
                copied
                  ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                  : 'bg-background hover:bg-accent text-foreground'
              )}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface EmailStatusBadgeProps {
  status: 'pending' | 'sent' | 'failed' | 'skipped';
}

function EmailStatusBadge({ status }: EmailStatusBadgeProps) {
  if (status === 'skipped') return null;

  const config = {
    pending: {
      label: 'Email pending',
      className: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    },
    sent: {
      label: 'Email sent',
      className: 'bg-green-500/10 text-green-600 dark:text-green-400',
    },
    failed: {
      label: 'Email failed',
      className: 'bg-red-500/10 text-red-600 dark:text-red-400',
    },
  }[status];

  if (!config) return null;

  return (
    <span className={cn('text-xs px-1.5 py-0.5 rounded', config.className)}>
      {config.label}
    </span>
  );
}

// ============================================================================
// Icons
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function XCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function DebugIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
      />
    </svg>
  );
}
